import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI, categoriesAPI } from '../services/api';
import ProductCard from '../components/ProductCard';
import { HiOutlineAdjustments, HiX, HiChevronDown } from 'react-icons/hi';

const Shop = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pagination, setPagination] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || '-createdAt',
    featured: searchParams.get('featured') || '',
    newArrival: searchParams.get('newArrival') || '',
    bestSeller: searchParams.get('bestSeller') || '',
    search: searchParams.get('search') || '',
    page: parseInt(searchParams.get('page')) || 1,
  });

  const sortOptions = [
    { value: '-createdAt', label: t('shop.newest') },
    { value: 'createdAt', label: t('shop.oldest') },
    { value: 'price', label: t('shop.priceLowHigh') },
    { value: '-price', label: t('shop.priceHighLow') },
    { value: '-averageRating', label: t('shop.topRated') },
    { value: 'name', label: t('shop.nameAZ') },
    { value: '-name', label: t('shop.nameZA') },
  ];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: 12,
        sort: filters.sort,
      };

      if (filters.category) params.category = filters.category;
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;
      if (filters.featured) params.isFeatured = true;
      if (filters.newArrival) params.isNew = true;
      if (filters.bestSeller) params.isBestSeller = true;
      if (filters.search) params.search = filters.search;

      const response = await productsAPI.getAll(params);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts]);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '-createdAt' && value !== 1) {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      minPrice: '',
      maxPrice: '',
      sort: '-createdAt',
      featured: '',
      newArrival: '',
      bestSeller: '',
      search: '',
      page: 1,
    });
  };

  const activeFiltersCount = [
    filters.category,
    filters.minPrice,
    filters.maxPrice,
    filters.featured,
    filters.newArrival,
    filters.bestSeller,
  ].filter(Boolean).length;

  return (
    <div className="animate-fade-in">
      {/* Hero Banner */}
      <section className="relative h-[30vh] md:h-[40vh] bg-primary-100">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4">{t('nav.shop')}</h1>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-luxury-gold transition-colors">{t('nav.home')}</Link>
              <span>/</span>
              <span>{t('nav.shop')}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:flex lg:gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              {/* Categories */}
              <div>
                <h3 className="font-serif text-lg mb-4">{t('shop.categories')}</h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => handleFilterChange('category', '')}
                      className={`text-sm hover:text-luxury-gold transition-colors ${
                        !filters.category ? 'text-luxury-gold font-medium' : 'text-gray-600'
                      }`}
                    >
                      {t('shop.allProducts')}
                    </button>
                  </li>
                  {categories.map((category) => (
                    <li key={category._id}>
                      <button
                        onClick={() => handleFilterChange('category', category.slug)}
                        className={`text-sm hover:text-luxury-gold transition-colors ${
                          filters.category === category.slug ? 'text-luxury-gold font-medium' : 'text-gray-600'
                        }`}
                      >
                        {category.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price Range */}
              <div>
                <h3 className="font-serif text-lg mb-4">{t('shop.priceRange')}</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder={t('shop.min')}
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-luxury-gold"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder={t('shop.max')}
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-luxury-gold"
                  />
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <h3 className="font-serif text-lg mb-4">{t('shop.collections')}</h3>
                <div className="space-y-2">
                  {[
                    { key: 'featured', label: t('shop.featured') },
                    { key: 'newArrival', label: t('shop.newArrivals') },
                    { key: 'bestSeller', label: t('shop.bestSellers') },
                  ].map((item) => (
                    <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!filters[item.key]}
                        onChange={(e) => handleFilterChange(item.key, e.target.checked ? 'true' : '')}
                        className="w-4 h-4 text-luxury-gold border-gray-300 focus:ring-luxury-gold"
                      />
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-luxury-gold transition-colors underline"
                >
                  {t('shop.clearFilters')}
                </button>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden flex items-center gap-2 text-sm"
              >
                <HiOutlineAdjustments className="w-5 h-5" />
                {t('shop.filters')}
                {activeFiltersCount > 0 && (
                  <span className="bg-luxury-gold text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Results Count */}
              <p className="text-sm text-gray-500 hidden lg:block">
                {t('shop.showing')} {products.length} {t('shop.of')} {pagination.total || 0} {t('shop.products')}
              </p>

              {/* Sort */}
              <div className="relative">
                <select
                  value={filters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                  className="appearance-none bg-transparent text-sm pr-8 focus:outline-none cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <HiChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner" />
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                  {products.map((product) => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handleFilterChange('page', page)}
                        className={`w-10 h-10 flex items-center justify-center text-sm transition-colors ${
                          filters.page === page
                            ? 'bg-luxury-charcoal text-white'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <h3 className="font-serif text-2xl mb-4">{t('shop.noProducts')}</h3>
                <p className="text-gray-500 mb-6">{t('shop.adjustFilters')}</p>
                <button
                  onClick={clearFilters}
                  className="inline-block bg-luxury-charcoal text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
                >
                  {t('shop.clearFilters')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden ${filtersOpen ? '' : 'pointer-events-none'}`}>
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            filtersOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setFiltersOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-80 bg-white transform transition-transform duration-300 ${
            filtersOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-serif text-lg">{t('shop.filters')}</h3>
            <button onClick={() => setFiltersOpen(false)}>
              <HiX className="w-6 h-6" />
            </button>
          </div>

          <div className="p-4 space-y-6 overflow-y-auto h-[calc(100%-60px)]">
            {/* Categories */}
            <div>
              <h4 className="font-medium mb-3">{t('shop.categories')}</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => handleFilterChange('category', '')}
                    className={`text-sm ${!filters.category ? 'text-luxury-gold font-medium' : ''}`}
                  >
                    {t('shop.allProducts')}
                  </button>
                </li>
                {categories.map((category) => (
                  <li key={category._id}>
                    <button
                      onClick={() => handleFilterChange('category', category.slug)}
                      className={`text-sm ${
                        filters.category === category.slug ? 'text-luxury-gold font-medium' : ''
                      }`}
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Price */}
            <div>
              <h4 className="font-medium mb-3">{t('shop.priceRange')}</h4>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder={t('shop.min')}
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="w-full px-3 py-2 border text-sm focus:outline-none focus:border-luxury-gold"
                />
                <span>-</span>
                <input
                  type="number"
                  placeholder={t('shop.max')}
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="w-full px-3 py-2 border text-sm focus:outline-none focus:border-luxury-gold"
                />
              </div>
            </div>

            {/* Collections */}
            <div>
              <h4 className="font-medium mb-3">{t('shop.collections')}</h4>
              <div className="space-y-2">
                {[
                  { key: 'featured', label: t('shop.featured') },
                  { key: 'newArrival', label: t('shop.newArrivals') },
                  { key: 'bestSeller', label: t('shop.bestSellers') },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!filters[item.key]}
                      onChange={(e) => handleFilterChange(item.key, e.target.checked ? 'true' : '')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t space-y-3">
              <button
                onClick={() => setFiltersOpen(false)}
                className="w-full bg-luxury-charcoal text-white py-3 text-sm uppercase tracking-widest"
              >
                {t('shop.applyFilters')}
              </button>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="w-full border border-gray-200 py-3 text-sm uppercase tracking-widest"
                >
                  {t('shop.clearAll')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
